import { apiSlice } from "./apiSlice";

/**
 * Platforms that carry a published-version row. One per store: each ships an
 * independent build with its own version number and listing URL.
 */
export type AppVersionPlatform =
  | "IOS"
  | "ANDROID"
  | "PROFESORES_IOS"
  | "PROFESORES_ANDROID"
  | "DESKTOP_MAC"
  | "DESKTOP_WINDOWS";

/** One row of the AppVersion table. */
export interface AppVersionEntry {
  id: number;
  platform: AppVersionPlatform;
  /**
   * Version published in the store, e.g. "1.2.0".
   *
   * Read-only for every platform the backend can resolve on its own: it
   * refreshes this from the App Store on each request (SCRUM-927).
   */
  version: string;
  /**
   * Lowest version still allowed to keep working, or null for "nothing is
   * forced". The one field a person still owns — no store can know whether an
   * update is compulsory.
   */
  minVersion: string | null;
  /** Where the update button sends the user. Empty means "no listing yet". */
  storeUrl: string;
  /** Master switch: with this off the client never shows the update notice. */
  showModal: boolean;
  /** When true the notice is mandatory — no way to dismiss it. */
  forceUpdate: boolean;
  created_at: string;
  updated_at: string;
}

/** Every field is optional: the form saves only what changed. */
export interface UpdateAppVersionArgs {
  platform: AppVersionPlatform;
  body: {
    version?: string;
    minVersion?: string | null;
    storeUrl?: string;
    showModal?: boolean;
    forceUpdate?: boolean;
  };
}

export const appVersionApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAppVersions: builder.query<AppVersionEntry[], void>({
      query: () => ({
        url: "/app-version",
        method: "GET",
      }),
      providesTags: [{ type: "AppVersion" as const, id: "LIST" }],
    }),
    updateAppVersion: builder.mutation<AppVersionEntry, UpdateAppVersionArgs>({
      query: ({ platform, body }) => ({
        url: `/app-version/${platform}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: "AppVersion", id: "LIST" }],
    }),
  }),
});

export const { useGetAppVersionsQuery, useUpdateAppVersionMutation } =
  appVersionApi;
